"use client";

import Link from "next/link";
import { OpenButton } from "./overlays";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { open as openCases, sell } from "@/app/actions";
import type { Item } from "@/lib/cases";
import { fmt } from "@/lib/money";
import ItemCard from "./item-card";
import { useLang, useT } from "./lang";
import { PD } from "./play-dict";

// Client never imports runtime values from lib/cases (it pulls the DB); these mirror it.
const DAILY_MS = 24 * 3600 * 1000;
// Strip: IDLE tiles show before a spin (tile MID centred); a spin starts from that exact view and lands on WIN.
const STRIP = 84, WIN = 74, IDLE = 15, MID = 7;
// Reel timing: long deceleration, a short overshoot-and-settle (real reel physics), then a pause to enjoy the landing.
const TIMING = {
  normal: { spin: 6200, settle: 450, stagger: 220, pause: 1300 },
  fast: { spin: 1400, settle: 200, stagger: 80, pause: 500 },
  still: { spin: 0, settle: 0, stagger: 0, pause: 0 },
};
type Timing = (typeof TIMING)["normal"];
const GOLD = "#e4ae39"; // knives & gloves: shown as "★ Rare item" in the strip until they land, CS-style
const RARE = new Set([GOLD, "#eb4b4b", "#d32ce6"]);
const isGold = (it: Item) => it.r.toLowerCase() === GOLD;

type PoolItem = { id: string; t: number; it: Item };
type Won = { id: number; item: string; value: number };
type Labels = { rare: string; special: string };

/** Visual reel only — the result is already final server-side. sqrt weighting lets rare items flash past more often than their real odds. */
function picker(pool: PoolItem[], rnd: () => number) {
  const w = pool.map((p) => Math.sqrt(p.t));
  const sum = w.reduce((a, b) => a + b, 0);
  return () => {
    let r = rnd() * sum;
    for (let k = 0; k < pool.length; k++) if ((r -= w[k]) < 0) return pool[k].id;
    return pool[0].id;
  };
}

/** Idle strip, identical on server and client: the same weighting driven by a slug-seeded LCG. */
function idleStrip(pool: PoolItem[], seed: string, k: number) {
  let h = k + 7;
  for (const ch of seed) h = (Math.imul(h, 31) + ch.charCodeAt(0)) >>> 0;
  const pick = picker(pool, () => (h = (Math.imul(h, 1103515245) + 12345) >>> 0) / 2 ** 32);
  return Array.from({ length: IDLE }, pick);
}

function buildStrip(pool: PoolItem[], winId: string, start: string[]) {
  const pick = picker(pool, Math.random);
  const s = Array.from({ length: STRIP }, (_, k) => start[k] ?? pick());
  s[WIN] = winId;
  return s;
}

/** Tiny WebAudio clicks. Off by default; the choice is remembered in this browser only. */
let audio: AudioContext | null = null;
function beep(freq: number, ms: number, vol = 0.035) {
  if (!audio) return;
  const o = audio.createOscillator(), g = audio.createGain(), now = audio.currentTime;
  o.type = "square";
  o.frequency.value = freq;
  g.gain.setValueAtTime(vol, now);
  g.gain.exponentialRampToValueAtTime(0.0001, now + ms / 1000);
  o.connect(g).connect(audio.destination);
  o.start(now);
  o.stop(now + ms / 1000);
}

function useNow(on: boolean) {
  const [now, setNow] = useState(0); // 0 until mounted, avoids a server/client clock mismatch
  useEffect(() => {
    if (!on) return;
    setNow(Date.now());
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [on]);
  return now;
}

/** A toggle remembered in this browser (localStorage can throw in private mode). */
function useStored(key: string) {
  const [on, setOn] = useState(false);
  useEffect(() => { try { setOn(localStorage.getItem(key) === "1"); } catch {} }, [key]);
  const toggle = () => {
    setOn(!on);
    try { localStorage.setItem(key, on ? "0" : "1"); } catch {}
  };
  return [on, toggle] as const;
}

const hms = (ms: number) => {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return [s / 3600, (s % 3600) / 60, s % 60].map((n) => String(Math.floor(n)).padStart(2, "0")).join(":");
};

export default function CaseOpener({ slug, name, art, kicker, exp, opened, facts, price, gems, daily, max, loggedIn, readyAt, pool }: {
  slug: string; name: string; art: string; kicker: string; exp: number; opened: number; facts: React.ReactNode;
  price: number; gems?: number; daily: boolean; max: number; loggedIn: boolean; readyAt: number; pool: PoolItem[];
}) {
  const t = useT(PD);
  const lang = useLang();
  const byId = useMemo(() => new Map(pool.map((p) => [p.id, p.it])), [pool]);
  // Hide knives/gloves behind the gold tile only where they're the jackpot, not in cases made of nothing else.
  const hideGold = useMemo(() => pool.filter((p) => isGold(p.it)).length * 2 < pool.length, [pool]);
  const [count, setCount] = useState(1);
  const [phase, setPhase] = useState<"idle" | "spin" | "done">("idle");
  const [won, setWon] = useState<Won[]>([]);
  const [strips, setStrips] = useState<string[][]>([]);
  const [spin, setSpin] = useState(0); // remounts the reels for every spin
  const [timing, setTiming] = useState<Timing>(TIMING.normal);
  const [sold, setSold] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [ready, setReady] = useState(readyAt);
  const [copied, setCopied] = useState(false);
  const [sound, toggleSound] = useStored("dd_sound");
  const [fast, toggleFast] = useStored("dd_fast");
  const landed = useRef(0);
  const now = useNow(daily && loggedIn);
  const waiting = daily && loggedIn && (now === 0 || ready > now);
  const idle = useMemo(() => Array.from({ length: count }, (_, k) => idleStrip(pool, slug, k)), [pool, slug, count]);
  const labels: Labels = { rare: t.rare_item, special: t.rare_special };

  // 1 gem per whole coin spent (lib/economy onSpend); gem-priced and free cases earn none.
  const gemsGet = gems || daily ? 0 : Math.floor((price * count) / 100);
  const forms = t.gems_forms.split("|");
  const form = (n: number) => { const c = new Intl.PluralRules(lang).select(n); return forms[c === "one" ? 0 : c === "few" ? 1 : 2]; };

  async function go() {
    setError("");
    setBusy(true);
    if (sound && !audio) audio = new AudioContext(); // must start inside the click
    const r = await openCases(slug, count);
    setBusy(false);
    if ("error" in r) { setError(r.error); return; }
    // Each new spin starts from what the reel shows now: the idle strip, or the last landing.
    const from = (k: number) => (phase === "done" && strips[k] ? strips[k].slice(WIN - MID, WIN - MID + IDLE) : idle[k] ?? idleStrip(pool, slug, k));
    setWon(r.drops);
    setSold(new Set());
    setStrips(r.drops.map((d, k) => buildStrip(pool, d.item, from(k))));
    setTiming(matchMedia("(prefers-reduced-motion: reduce)").matches ? TIMING.still : fast ? TIMING.fast : TIMING.normal);
    setSpin((n) => n + 1);
    if (daily) setReady(Date.now() + DAILY_MS);
    landed.current = 0;
    setPhase("spin");
  }

  const onLanded = () => {
    if (++landed.current === strips.length) setTimeout(() => setPhase("done"), timing.pause);
  };

  async function sellIds(ids: number[]) {
    setError("");
    setBusy(true);
    const r = await sell(ids);
    setBusy(false);
    if ("error" in r) { setError(r.error ?? t.err_generic); return; }
    setSold((s) => new Set([...s, ...ids]));
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {}
  }

  const unsold = won.filter((d) => !sold.has(d.id));
  const locked = busy || phase === "spin";
  const isRare = (d: Won) => RARE.has(byId.get(d.item)!.r.toLowerCase()) || (price > 0 && d.value >= price * 3);
  const shown = phase === "idle" ? idle.length : strips.length;

  return (
    <section className="case-stage">
      <header className="case-head">
        <Link href="/" className="case-back">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="m15 18-6-6 6-6" /></svg>
          {t.back_btn}
        </Link>
        <div className="flex min-w-0 flex-1 items-center gap-3 sm:flex-none">
          <img src={art} alt={`${name} case`} className="case-thumb case-float" />
          <div className="min-w-0">
            <p className="kicker !text-accent">{kicker}</p>
            <h1 className="case-name">{name}</h1>
            <p className="case-meta">
              {exp > 0 && <><span className="text-accent">+{exp.toLocaleString("en-US")} EXP</span><span className="case-meta-sep" aria-hidden /></>}
              <span>{t.opened}: <b className="tnum text-text">{opened.toLocaleString("en-US")}</b></span>
            </p>
          </div>
        </div>
        <span className="case-rule" aria-hidden />
        <div className="case-tools">
          <button type="button" onClick={copy} className="case-tool case-copy" data-done={copied || undefined}>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              {copied ? <path d="m5 12 5 5L20 7" /> : <><rect x="9" y="9" width="12" height="12" rx="2" /><path d="M5 15V5a2 2 0 0 1 2-2h10" /></>}
            </svg>
            <span aria-live="polite">{copied ? t.copied : t.copy_link}</span>
          </button>
          <button type="button" onClick={toggleSound} className="case-tool" aria-pressed={sound} title={sound ? t.sound_on : t.sound_off} aria-label={sound ? t.sound_on : t.sound_off}>
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M11 5 6 9H3v6h3l5 4z" />
              {sound ? <path d="M15.5 8.5a5 5 0 0 1 0 7M18.5 5.5a9 9 0 0 1 0 13" /> : <path d="m16 9 5 6M21 9l-5 6" />}
            </svg>
          </button>
          <button type="button" onClick={toggleFast} className="case-tool" aria-pressed={fast} title={fast ? t.fast_on : t.fast_off} aria-label={fast ? t.fast_on : t.fast_off}>
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M4.5 16.5c-1.5 1.3-2 5-2 5s3.7-.5 5-2c.7-.8.7-2.1-.1-2.9a2.2 2.2 0 0 0-2.9-.1z" />
              <path d="m12 15-3-3a22 22 0 0 1 2-3.9A12.9 12.9 0 0 1 22 2c0 2.7-.8 7.5-6 11a22.4 22.4 0 0 1-4 2z" />
              <path d="M9 12H4s.6-3 2-4c1.6-1.1 5 0 5 0M12 15v5s3-.6 4-2c1.1-1.6 0-5 0-5" />
            </svg>
          </button>
          <span className="case-18" role="img" aria-label={t.adults} title={t.adults}>18<sup>+</sup></span>
        </div>
      </header>

      {gemsGet > 0 && (
        <p className="case-gems">
          <svg viewBox="0 0 32 32" width="34" height="34" aria-hidden>
            <path fill="#7d2fd0" d="M16 2 30 12 25 29H7L2 12z" />
            <path fill="#b055ff" d="M16 2 30 12 16 17 2 12z" />
            <path fill="#d9a6ff" d="m16 2 6 10-6 5-6-5z" />
            <path fill="#5d1fa3" d="M16 17 25 29H7z" />
          </svg>
          <span>{t.gems_line}<b className="tnum">{gemsGet.toLocaleString("en-US")} {form(gemsGet)}!</b></span>
        </p>
      )}

      <div className="reels" data-n={shown}>
        {phase === "idle"
          ? idle.map((s, k) => (
              <div key={k} className="reel reel-mask reel-idle">
                <div className="reel-track">{s.map((id, j) => <Tile key={j} it={byId.get(id)!} hide={hideGold} labels={labels} />)}</div>
                <div className="reel-marker" aria-hidden />
              </div>
            ))
          : strips.map((s, k) => (
              <Reel key={`${spin}-${k}`} strip={s} byId={byId} timing={timing} delay={k * timing.stagger} rare={isRare(won[k])} hideGold={hideGold} labels={labels} sound={sound} onLanded={onLanded} />
            ))}
      </div>

      {phase === "done" && (
        <div className="outcome-pop mt-6">
          <p className="kicker mb-3 text-center">{won.length > 1 ? t.your_drops : t.you_got}</p>
          <div className="mx-auto grid max-w-4xl grid-cols-2 justify-center gap-2.5 sm:grid-cols-[repeat(auto-fit,minmax(150px,180px))]">
            {won.map((d) => (
              <ItemCard
                key={d.id}
                item={byId.get(d.item)!}
                value={d.value}
                muted={sold.has(d.id)}
                foot={sold.has(d.id)
                  ? <div className="py-1.5 text-center text-xs font-bold uppercase text-dim">{t.sold}</div>
                  : <button onClick={() => sellIds([d.id])} disabled={busy} className="btn btn-money w-full py-1.5 text-xs">{t.sell} <span className="coin">{fmt(d.value)}</span></button>}
              />
            ))}
          </div>
        </div>
      )}

      <div className="mt-6 flex flex-wrap items-center justify-center gap-2.5">
        {!loggedIn ? (
          <OpenButton what="login" className="btn btn-accent h-12 px-7 text-sm">{t.login_open}</OpenButton>
        ) : phase === "done" ? (
          <>
            {unsold.length > 1 && (
              <button onClick={() => sellIds(unsold.map((d) => d.id))} disabled={busy} className="btn btn-money h-12 px-5 text-sm">
                {t.sell_all} <span className="coin">{fmt(unsold.reduce((s, d) => s + d.value, 0))}</span>
              </button>
            )}
            <button onClick={() => setPhase("idle")} disabled={busy} className="btn btn-ghost h-12 px-5 text-sm">{t.keep}</button>
            {!daily && (
              <button onClick={go} disabled={busy} className="btn btn-accent h-12 px-6 text-sm">
                {t.open_again} {gems ? <span className="gem-amount">{gems * count}</span> : <span className="coin">{fmt(price * count)}</span>}
              </button>
            )}
          </>
        ) : (
          <>
            {max > 1 && (
              <div className="segmented" role="group" aria-label={t.how_many}>
                {Array.from({ length: max }, (_, k) => k + 1).map((n) => (
                  <button key={n} onClick={() => setCount(n)} data-active={count === n} disabled={locked} className="min-w-11 tnum">x{n}</button>
                ))}
              </div>
            )}
            {waiting ? (
              <button disabled className="btn btn-ghost h-12 px-7 text-sm">{t.next_free} <span className="tnum">{now ? hms(ready - now) : "…"}</span></button>
            ) : (
              <button onClick={go} disabled={locked} className="btn btn-accent h-12 min-w-56 px-7 text-sm">
                {phase === "spin" ? t.opening : daily ? t.open_free : <>{t.open_for} {gems ? <span className="gem-amount">{gems * count}</span> : <span className="coin">{fmt(price * count)}</span>}</>}
              </button>
            )}
          </>
        )}
      </div>

      {error && (
        <p className="mt-3 text-center text-sm font-semibold text-danger">
          {error}{/insufficient/i.test(error) && <> <Link href="/payments" className="font-normal text-muted underline underline-offset-2">{t.top_up}</Link></>}
          {/gems/i.test(error) && <> <Link href="/event" className="font-normal text-muted underline underline-offset-2">{t.earn_gems}</Link></>}
        </p>
      )}

      <div className="case-facts">{facts}</div>
    </section>
  );
}

/** One reel tile: item art, weapon and skin name in its rarity colour; knives/gloves as a gold "★ Rare item" until revealed. */
function Tile({ it, hide, labels, win, reveal }: { it: Item; hide: boolean; labels: Labels; win?: boolean; reveal?: boolean }) {
  const gold = hide && isGold(it) && !reveal;
  const [weapon, skin] = it.n.replace("StatTrak™ ", "").split(" | ");
  return (
    <div className={`reel-tile rarity-tile ${gold ? "reel-gold" : ""} ${win ? "reel-win" : ""} ${win && reveal && hide && isGold(it) ? "reel-reveal" : ""}`} style={{ "--rc": it.r } as React.CSSProperties}>
      {gold ? (
        <>
          <svg viewBox="0 0 64 64" width="96" height="96" className="reel-gold-mark" aria-hidden>
            <path fill="#8a6418" d="M18 23Q8 21 1 16q11 1 23 2zM36 38 54 40.5 57 44 31 49z" />
            <path fill="#f2c24f" d="M63 32 61 35 36 38 31 49 29 57 15 63 14 53 6 50 11 45 3 40 11 37 4 30 12 29 18 23 22 17Q10 12 2 4q15 3 29 7l7 3 5 5 11 3 9 7z" />
            <path fill="#fff1c2" d="M31 11l7 3 5 5-9-4z" />
            <path fill="#3a2a08" d="m33 19 11 1-7 3.5zM57 27l3 1.2-3 .8z" />
          </svg>
          <span className="t-w">{labels.rare}</span>
          <span className="t-s">{labels.special}</span>
        </>
      ) : (
        <>
          <img src={it.i} alt="" draggable={false} fetchPriority="low" />
          <span className="t-w">{weapon}</span>
          {skin && <span className="t-s">{skin}</span>}
        </>
      )}
    </div>
  );
}

function Reel({ strip, byId, timing, delay, rare, hideGold, labels, sound, onLanded }: {
  strip: string[]; byId: Map<string, Item>; timing: Timing; delay: number; rare: boolean; hideGold: boolean; labels: Labels; sound: boolean; onLanded: () => void;
}) {
  const box = useRef<HTMLDivElement>(null);
  const track = useRef<HTMLDivElement>(null);
  const marker = useRef<HTMLDivElement>(null);
  const [landed, setLanded] = useState(false);
  const win = byId.get(strip[WIN])!;

  useLayoutEffect(() => {
    const tr = track.current!, w = tr.children[WIN] as HTMLElement, s = tr.children[MID] as HTMLElement, step = w.offsetWidth + 4;
    const half = box.current!.clientWidth / 2;
    const x0 = s.offsetLeft + s.offsetWidth / 2 - half; // same view as the idle strip
    const jitter = (Math.random() - 0.5) * w.offsetWidth * 0.7; // stop anywhere inside the winning tile
    const x = w.offsetLeft + w.offsetWidth / 2 + jitter - half;
    box.current!.style.setProperty("--off", `${-jitter}px`); // where that tile sits: the sight frame locks onto it
    tr.style.transition = "none";
    tr.style.transform = `translateX(${-(timing.spin ? x0 : x)}px)`;
    if (!timing.spin) { setLanded(true); onLanded(); return; }
    void tr.offsetWidth; // commit the start position before transitioning
    const over = step * 0.32;
    const timers = [
      setTimeout(() => {
        tr.style.transition = `transform ${timing.spin}ms cubic-bezier(.06,.62,.12,1)`;
        tr.style.transform = `translateX(${-(x + over)}px)`;
      }, delay),
      setTimeout(() => {
        tr.style.transition = `transform ${timing.settle}ms cubic-bezier(.4,0,.2,1)`;
        tr.style.transform = `translateX(${-x}px)`;
      }, delay + timing.spin),
      setTimeout(() => {
        setLanded(true);
        if (sound) { beep(rare ? 880 : 660, 120, 0.05); if (rare) setTimeout(() => beep(1320, 180, 0.05), 110); }
        onLanded();
      }, delay + timing.spin + timing.settle),
    ];

    // Tick: every time a new tile crosses the marker, nudge the marker (and click, if sound is on).
    let raf = 0, last = -1, lastClick = 0;
    const loop = () => {
      const idx = Math.floor((half - new DOMMatrixReadOnly(getComputedStyle(tr).transform).m41 - 4) / step);
      if (idx !== last) {
        last = idx;
        marker.current?.animate([{ transform: "translateX(-1px) scaleX(1)" }, { transform: "translateX(-1px) scaleX(2.6)" }, { transform: "translateX(-1px) scaleX(1)" }], { duration: 140 });
        const t = performance.now();
        if (sound && t - lastClick > 35) { beep(1900, 25); lastClick = t; }
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    const stop = setTimeout(() => cancelAnimationFrame(raf), delay + timing.spin + timing.settle);
    return () => { timers.forEach(clearTimeout); clearTimeout(stop); cancelAnimationFrame(raf); };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one spin per mount
  }, []);

  return (
    <div ref={box} className={`reel reel-mask ${landed ? "reel-landed" : ""}`} style={{ "--rc": win.r } as React.CSSProperties}>
      <div ref={track} className="reel-track">
        {strip.map((id, k) => <Tile key={k} it={byId.get(id)!} hide={hideGold} labels={labels} win={k === WIN} reveal={landed && k === WIN} />)}
      </div>
      <div ref={marker} className="reel-marker" aria-hidden />
      {landed && rare && (
        <div className="reel-burst" aria-hidden>
          {Array.from({ length: 18 }, (_, k) => (
            <i key={k} style={{ "--a": `${k * 20 + ((k * 37) % 14)}deg`, "--d": `${48 + ((k * 53) % 70)}px`, "--s": `${(k * 97) % 360}deg` } as React.CSSProperties} />
          ))}
        </div>
      )}
    </div>
  );
}


